type IgnoredErrorMatcher =
  | { readonly everyFrame?: RegExp; readonly message: RegExp; }
  | { readonly everyFrame: RegExp; readonly message?: RegExp; };

/**
 * An error the app cannot act on, dropped before it reaches the console, logger, LogRocket and Sentry.
 * Every provided matcher must match. `everyFrame` must match each stack frame, so an error the app code took part in is kept.
 */
type IgnoredError = IgnoredErrorMatcher & {
  /** Why dropping it is safe. Keep it specific — this list is the only place the decision is documented. */
  readonly reason: string;
};

const STACK_FRAME_REGEX = /:\d+:\d+\)?$/;

const IGNORED_ERRORS: IgnoredError[] = [
  {
    // Not anchored at the start: in dev mode Angular prefixes the message with "An ErrorEvent with no error occurred…"
    message: /Script error\.$/,
    reason: 'Thrown by a cross-origin (third-party) script; the browser hides its message and stack, so nothing to act on.',
  },
  {
    message: /ResizeObserver loop/,
    reason: 'Benign browser notice that some resize notifications were postponed to the next frame; nothing breaks.',
  },
  {
    // Known gap: when an extension wraps `fetch`, a rejection consumed via `.then` (not `await`) carries only the
    // wrapper frame, so an app request failing that way is dropped too. HttpClient errors never reach this handler.
    everyFrame: /(chrome|moz|safari(-web)?)-extension:\/\//,
    reason: 'Thrown entirely inside a browser extension injected into the page, not by the app.',
  },
];

export function isIgnoredError(error: Error): boolean {
  const frames = error.stack?.split('\n').map((line) => line.trim()).filter((line) => STACK_FRAME_REGEX.test(line)) ?? [];

  return IGNORED_ERRORS.some(({ everyFrame, message }) => {
    const messageMatches = !message || message.test(error.message);
    const framesMatch = everyFrame ? frames.length > 0 && frames.every((frame) => everyFrame.test(frame)) : true;

    return messageMatches && framesMatch;
  });
}
